"use client";
import React, { useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Progress, Tooltip, Tag } from "antd";
import { 
  WarningOutlined, 
  CheckCircleOutlined,
  CloseCircleOutlined 
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import "./MedicalHistory.css";

import IconArrow2 from "../../../assets/icons/Icon-arrow2.png";
import BilanIcon from "../../../assets/icons/svg/medical-history-bilan.svg";
import IrmIcon from "../../../assets/icons/svg/medical-history-irm.svg";
import EchographyIcon from "../../../assets/icons/svg/medical-history-echography.svg";
import RadiographyIcon from "../../../assets/icons/svg/medical-history-radiography.svg";
import OrdonnanceIcon from "../../../assets/icons/svg/medical-history-ordonnance.svg";
import CompteRenduIcon from "../../../assets/icons/svg/medical-history-compte-rendu.svg";
import AutreIcon from "../../../assets/icons/svg/medical-history-autre.svg";
import useExtraction from "../../hooks/useExtraction";

// ========== NOMBRE MAX DE FICHIERS (QUOTA) ==========
const MAX_FILES = 10;

// ========== SEUILS DES 3 COULEURS ==========
// < 60%  -> vert   (bon)
// 60-89% -> orange (attention)
// >= 90% -> rouge  (presque plein)
const THRESHOLD_WARNING = 60;
const THRESHOLD_CRITICAL = 90;

// ========== ICON WRAPPER COMPONENT ==========
const IconWrapper = ({ src, alt = "icon" }) => (
  <img src={src} alt={alt} style={{ width: "24px", height: "24px" }} />
);

// ========== MAPPING TYPE → ICÔNE ==========
const TYPE_ICONS = {
  BILAN_BIOCHIMIQUE: <IconWrapper src={BilanIcon} alt="bilan" />,
  IRM: <IconWrapper src={IrmIcon} alt="irm" />,
  ECHOGRAPHIE: <IconWrapper src={EchographyIcon} alt="echography" />,
  RADIOGRAPHIE: <IconWrapper src={RadiographyIcon} alt="radiography" />,
  ORDONNANCE: <IconWrapper src={OrdonnanceIcon} alt="ordonnance" />,
  COMPTE_RENDU: <IconWrapper src={CompteRenduIcon} alt="compte-rendu" />,
  AUTRE: <IconWrapper src={AutreIcon} alt="autre" />,
};

const API_TYPE_ORDER = [
  "BILAN_BIOCHIMIQUE",
  "IRM",
  "ECHOGRAPHIE",
  "RADIOGRAPHIE",
  "ORDONNANCE",
  "COMPTE_RENDU",
  "AUTRE",
];

const buildStatsMap = (statistics) => {
  const byType = statistics?.by_type || {};
  const res = {};
  for (const [typeKey, obj] of Object.entries(byType)) {
    res[typeKey] = Number(obj?.count || 0);
  }
  return res;
};

const Badge = ({ icon }) => (
  <span className="mh-badge" aria-hidden>
    {icon}
  </span>
);

export default function MedicalHistory({
  onItemClick,
  userId = null,
  subprofileId = null,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { 
    statistics, 
    userStorage, 
    storageLoading, 
    getUserStorage 
  } = useExtraction(userId, { subprofileId });

  useEffect(() => {
    getUserStorage();
  }, [getUserStorage]);

  const statsMap = useMemo(() => buildStatsMap(statistics), [statistics]);

  // ========== QUOTA BASÉ SUR LE NOMBRE DE FICHIERS ==========
  const documentCount = userStorage?.document_count || 0;
  const maxFiles = userStorage?.max_files || MAX_FILES;
  const availableFiles = Math.max(maxFiles - documentCount, 0);
  const usagePercent = maxFiles > 0
    ? Math.min((documentCount / maxFiles) * 100, 100)
    : 0;

  // ========== 3 ÉTATS DE COULEUR ==========
  const getStorageStatus = () => {
    if (usagePercent >= THRESHOLD_CRITICAL) {
      return {
        color: '#ff4d4f',      // Rouge
        status: 'error',
        icon: <CloseCircleOutlined />,
        text: t('medicalHistory.almostFull'),
        level: 'critical',
      };
    }
    if (usagePercent >= THRESHOLD_WARNING) {
      return {
        color: '#faad14',      // Orange
        status: 'warning',
        icon: <WarningOutlined />,
        text: t('medicalHistory.highUsage'),
        level: 'warning',
      };
    }
    return {
      color: '#52c41a',        // Vert
      status: 'success',
      icon: <CheckCircleOutlined />,
      text: t('medicalHistory.good'),
      level: 'good',
    };
  };

  const storageStatus = getStorageStatus();

  const items = useMemo(() => {
    return API_TYPE_ORDER.map((typeKey) => {
      const icon = TYPE_ICONS[typeKey] || <IconWrapper src={AutreIcon} alt="autre" />;
      const count = statsMap[typeKey] ?? 0;
      return {
        key: typeKey,
        title: t(`medicalHistory.types.${typeKey}.title`),
        desc: t(`medicalHistory.types.${typeKey}.desc`),
        icon: icon,
        count,
      };
    });
  }, [statsMap, t]);

  const goTo = (typeKey) => {
    if (onItemClick) return onItemClick({ type: typeKey });
    navigate(`/records/${typeKey}`);
  };

  return (
    <section className="mh">
      <header className="mh-header">
        <h2>{t('medicalHistory.title')}</h2>
        
        <div className="mh-storage" aria-label={t('medicalHistory.storageUsed')}>
          <div className="mh-storage-main">
            <div className="mh-storage-text">
              <strong style={{ color: storageStatus.color }}>
                {documentCount}
              </strong>
              <span className="mh-storage-separator">/</span>
              <span className="mh-storage-quota">
                {maxFiles} {t('medicalHistory.files', { count: maxFiles })}
              </span>
              <Tag 
                color={storageStatus.status} 
                icon={storageStatus.icon}
                style={{ marginLeft: 8 }}
              >
                {storageStatus.text}
              </Tag>
            </div>
            
            <Tooltip 
              title={t('medicalHistory.storageTooltip', { 
                count: documentCount, 
                available: availableFiles 
              })}
            >
              <Progress
                percent={usagePercent}
                strokeColor={storageStatus.color}
                showInfo={true}
                format={() => `${documentCount}/${maxFiles}`}
                size="small"
                className={`mh-progress-${storageStatus.level}`}
                style={{ width: '250px' }}
              />
            </Tooltip>
          </div>
          
          {usagePercent >= THRESHOLD_WARNING && (
            <div className={`mh-storage-warning mh-storage-warning--${storageStatus.level}`}>
              <WarningOutlined /> 
              <span>
                {usagePercent >= THRESHOLD_CRITICAL
                  ? t('medicalHistory.storageCritical', { available: availableFiles })
                  : t('medicalHistory.storageWarning', { available: availableFiles })
                }
              </span>
            </div>
          )}
        </div>
      </header>

      <div className="mh-grid">
        {items.map((item) => (
          <article
            key={item.key}
            className="mh-card"
            onClick={() => goTo(item.key)}
            tabIndex={0}
            role="button"
            aria-label={t('medicalHistory.openType', { type: item.title })}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                goTo(item.key);
              }
            }}
          >
            <div className="mh-card-top">
              <Badge icon={item.icon} />
              <h3 className="mh-title">{item.title}</h3>
            </div>

            <p className="mh-desc">{item.desc}</p>

            <div className="mh-card-bottom">
              <span className="mh-count">
                {String(item.count).padStart(2, "0")} {t('medicalHistory.test', { count: item.count })}
                {item.count === 0 && <span className="mh-empty"> ({t('medicalHistory.empty')})</span>}
              </span>
              <button
                className="mh-arrow"
                aria-label={t('medicalHistory.openType', { type: item.title })}
                onClick={(e) => {
                  e.stopPropagation();
                  goTo(item.key);
                }}
              >
                <img src={IconArrow2} alt="" className="mh-arrow-icon" />
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
